// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI, supportsDesktopScreenShareAudioCapture} from '@app/features/ui/utils/NativeUtils';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import ScreenShareCodecNegotiation from '@app/features/voice/engine/ScreenShareCodecNegotiation';
import {resolveConfiguredScreenShareTarget} from '@app/features/voice/engine/voice_screen_share_manager/shared';
import ActiveScreenShareSource from '@app/features/voice/state/ActiveScreenShareSource';
import {clearDesktopSourceIntent, setDesktopSourceIntent} from '@app/features/voice/state/DesktopSourceIntent';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	filterRoutableLinuxAudioSources,
	LINUX_AUDIO_TARGET_OBJECTS_PATTERN_KEY,
	toNativeLinuxAudioPatterns,
} from '@app/features/voice/utils/LinuxAudioSourceRules';
import {
	armNativeAudioForLinuxRouting,
	armNativeAudioForNextCapture,
	armNativeSystemAudioForNextCapture,
	disarmNativeAudio,
	disarmPendingNativeAudio,
	getLastNativeAudioArmFailure,
	getNativeAudioAvailabilityCached,
} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {
	type ScreenShareAudioCaptureDebugInfo,
	ScreenShareAudioCaptureError,
} from '@app/features/voice/utils/ScreenShareAudioCaptureError';
import {findPairedDeviceShareAudioInput} from '@app/features/voice/utils/ScreenShareAudioSummary';
import {
	type DisplayShareEnvironment,
	getDisplayShareEnvironment,
	usesNativeDisplayShareAudioSelection,
} from '@app/features/voice/utils/ScreenShareEnvironment';
import {
	type BuiltScreenShareOptions,
	buildScreenShareOptions,
	type ScreenShareContext,
	type ScreenShareTarget,
} from '@app/features/voice/utils/ScreenShareOptions';
import {
	isScreenSharePortalUnavailableError,
	ScreenSharePortalUnavailableError,
} from '@app/features/voice/utils/ScreenSharePortalUnavailableError';
import {isScreenShareRollbackIncompleteError} from '@app/features/voice/utils/ScreenShareRollbackIncompleteError';
import {executeScreenShareOperation, handleScreenShareError} from '@app/features/voice/utils/ScreenShareUtils';
import {
	type AppShareAudioRoute,
	resolveWindowShareAudioScope,
	routesManualAudioSources,
	type StreamSettingsShareContext,
	selectAppShareAudioRoute,
	type WindowShareAudioScope,
} from '@app/features/voice/utils/StreamSettingsUpdatePolicy';
import type {NativeAudioStartOptions, VirtmicNode} from '@app/types/electron.d';
import type {ScreenShareCaptureOptions, VideoCodec} from 'livekit-client';

const logger = new Logger('ScreenShareStartFlow');

type LinuxNativeAudioRule = NonNullable<NativeAudioStartOptions['linuxRule']>;

interface LinuxAudioLinkOptions {
	ignoreInputMedia: boolean;
	ignoreVirtual: boolean;
	ignoreDevices: boolean;
}

function getLinkOptions(): LinuxAudioLinkOptions {
	return {
		ignoreInputMedia: VoiceSettings.getLinuxAudioCaptureIgnoreInputMedia(),
		ignoreVirtual: VoiceSettings.getLinuxAudioCaptureIgnoreVirtual(),
		ignoreDevices: VoiceSettings.getLinuxAudioCaptureIgnoreDevices(),
	};
}

function getSystemOptions() {
	return {
		...getLinkOptions(),
		onlySpeakers: VoiceSettings.getLinuxAudioCaptureOnlySpeakers(),
		onlyDefaultSpeakers: VoiceSettings.getLinuxAudioCaptureOnlyDefaultSpeakers(),
	};
}

function withNativeAudioExcludes(exclude: Array<VirtmicNode>, options: LinuxAudioLinkOptions): Array<VirtmicNode> {
	const next = toNativeLinuxAudioPatterns(exclude);
	if (options.ignoreVirtual) {
		next.push({'node.virtual': 'true'});
	}
	return next;
}

function buildLinuxNativeAudioRule(
	sourceMode: 'system' | 'specific',
	userIncludeSources: Array<VirtmicNode>,
	userExcludeSources: Array<VirtmicNode>,
): LinuxNativeAudioRule {
	const linkOptions = getLinkOptions();
	const nativeIncludeSources = toNativeLinuxAudioPatterns(userIncludeSources);
	if (sourceMode === 'specific' && nativeIncludeSources.length > 0) {
		const includesDeviceTarget = nativeIncludeSources.some(
			(source) => LINUX_AUDIO_TARGET_OBJECTS_PATTERN_KEY in source,
		);
		return {
			include: nativeIncludeSources,
			exclude: withNativeAudioExcludes([], linkOptions),
			ignoreInputMedia: linkOptions.ignoreInputMedia,
			ignoreDevices: includesDeviceTarget ? false : linkOptions.ignoreDevices,
		};
	}
	const systemOptions = getSystemOptions();
	return {
		include: [],
		exclude: withNativeAudioExcludes(userExcludeSources, linkOptions),
		ignoreInputMedia: systemOptions.ignoreInputMedia,
		ignoreDevices: systemOptions.ignoreDevices,
		onlySpeakers: systemOptions.onlySpeakers,
		onlyDefaultSpeakers: systemOptions.onlyDefaultSpeakers,
	};
}

export async function reconfigureActiveLinuxScreenShareAudioLink(): Promise<boolean> {
	const electronApi = getElectronAPI();
	const virtmicApi = electronApi?.virtmic;
	if (electronApi?.platform !== 'linux') {
		return false;
	}
	const sourceMode = VoiceSettings.getEffectiveScreenShareAudioSourceMode();
	const userIncludeSources = VoiceSettings.getEffectiveScreenShareAudioIncludeSources().map((entry) => ({...entry}));
	const userExcludeSources = VoiceSettings.getEffectiveScreenShareAudioExcludeSources().map((entry) => ({...entry}));
	if (sourceMode === 'none') {
		disarmNativeAudio();
		await virtmicApi?.stop();
		return true;
	}
	const nativeRule = buildLinuxNativeAudioRule(sourceMode, userIncludeSources, userExcludeSources);
	if (await MediaEngine.ensureLinuxScreenShareAudioPublication(nativeRule).catch(() => false)) {
		await virtmicApi?.stop();
		return true;
	}
	disarmNativeAudio();
	return false;
}

async function getManualAudioSourceSelectionInput(shareContext: StreamSettingsShareContext) {
	const platform = getElectronAPI()?.platform;
	return {
		platform,
		shareContext,
		nativeAudioAvailability: platform === 'linux' ? await getNativeAudioAvailabilityCached() : null,
		audioSourceMode: VoiceSettings.getScreenShareAudioSourceMode(),
		selectedSourceCount: countRoutableAudioSources(),
		usesDeviceMicrophone: VoiceSettings.getScreenShareDeviceAudioUsesMicrophone(),
	};
}

async function shouldRouteManualAudioSourcesForShare(shareContext: StreamSettingsShareContext): Promise<boolean> {
	return routesManualAudioSources(await getManualAudioSourceSelectionInput(shareContext));
}

function appShareAudioRouteToSourceMode(route: AppShareAudioRoute | null): 'none' | 'system' | 'specific' | null {
	if (route === 'none') return 'none';
	if (route === 'apps') return 'specific';
	if (route === 'system') return 'system';
	return null;
}

function countRoutableAudioSources(): number {
	return filterRoutableLinuxAudioSources(VoiceSettings.getScreenShareAudioIncludeSources()).length;
}

async function resolveAppShareAudioScope(requestedScope: WindowShareAudioScope): Promise<WindowShareAudioScope> {
	return resolveWindowShareAudioScope({
		shareContext: 'app',
		displayShareEnvironment: await getDisplayShareEnvironment(),
		windowAudioScope: requestedScope,
	});
}

function selectAppShareAudioRouteForScope(scope: WindowShareAudioScope): AppShareAudioRoute {
	return selectAppShareAudioRoute({
		audioSourceMode: VoiceSettings.getEffectiveScreenShareAudioSourceMode(),
		selectedSourceCount: countRoutableAudioSources(),
		windowAudioScope: scope,
	});
}

export async function reconfigureActiveLinuxAppShareAudio(
	requestedWindowAudioScope?: WindowShareAudioScope,
): Promise<boolean> {
	const electronApi = getElectronAPI();
	if (electronApi?.platform !== 'linux') return false;
	if (ActiveScreenShareSource.isOwnWindow()) {
		logger.warn('Refusing to route audio into a Fluxer-owned window share');
		await stopActiveLinuxScreenShareAudioLink();
		return false;
	}
	const scope = await resolveAppShareAudioScope(
		requestedWindowAudioScope ?? ActiveScreenShareSource.getWindowAudioScope(),
	);
	if (selectAppShareAudioRouteForScope(scope) !== 'window') {
		return reconfigureActiveLinuxScreenShareAudioLink();
	}
	const sourceId = ActiveScreenShareSource.getSourceId();
	if (sourceId == null) {
		logger.warn('Cannot route the shared window own audio without a published window source');
		disarmNativeAudio();
		return false;
	}
	if (await MediaEngine.ensureWindowScreenShareAudioPublication(sourceId).catch(() => false)) {
		return true;
	}
	logger.warn('Failed to narrow the live share back to the shared window own audio; dropping its audio instead', {
		sourceId,
	});
	disarmNativeAudio();
	return false;
}

export async function applyLiveScreenShareAudioSourceChange(
	shareContext: StreamSettingsShareContext,
	requestedWindowAudioScope?: WindowShareAudioScope,
): Promise<boolean> {
	if (shareContext === 'device') return reconfigureActiveDeviceShareAudio();
	if (shareContext !== 'app') return reconfigureActiveLinuxScreenShareAudioLink();
	const applied = await reconfigureActiveLinuxAppShareAudio(requestedWindowAudioScope).catch((error) => {
		logger.warn('Failed to apply the window share audio scope', {error, requestedWindowAudioScope});
		return false;
	});
	if (applied && requestedWindowAudioScope != null) {
		ActiveScreenShareSource.setWindowAudioScope(requestedWindowAudioScope);
	}
	return applied;
}

export async function stopActiveLinuxScreenShareAudioLink(): Promise<boolean> {
	const electronApi = getElectronAPI();
	const virtmicApi = electronApi?.virtmic;
	if (electronApi?.platform !== 'linux') {
		return false;
	}
	disarmNativeAudio();
	await virtmicApi?.stop();
	return true;
}

function didScreenShareStart(): boolean {
	return Boolean(MediaEngine.room?.localParticipant?.isScreenShareEnabled || LocalVoiceState.getSelfStream());
}

function shouldIncludeAudioForShare(
	shareContext: ScreenShareContext,
	displayShareEnvironment: DisplayShareEnvironment,
	sourceId?: string | null,
	preferredDisplaySurface?: 'window' | 'monitor',
): boolean {
	if (shareContext === 'device') {
		return VoiceSettings.getShareDeviceAudio();
	}
	if (displayShareEnvironment === 'web') {
		return supportsDesktopScreenShareAudioCapture();
	}
	if (sourceId?.startsWith('window:')) {
		return supportsDesktopScreenShareAudioCapture() && VoiceSettings.getShareAppAudio();
	}
	if (sourceId?.startsWith('screen:')) {
		return supportsDesktopScreenShareAudioCapture() && VoiceSettings.getShareDesktopAudio();
	}
	if (preferredDisplaySurface === 'window') {
		return supportsDesktopScreenShareAudioCapture() && VoiceSettings.getShareAppAudio();
	}
	if (shareContext === 'display' && usesNativeDisplayShareAudioSelection(displayShareEnvironment)) {
		return supportsDesktopScreenShareAudioCapture() && VoiceSettings.getShareDesktopAudio();
	}
	return supportsDesktopScreenShareAudioCapture() && VoiceSettings.getShareDesktopAudio();
}

function removeAudioFromCaptureOptions(captureOptions: ScreenShareCaptureOptions): void {
	captureOptions.audio = false;
	captureOptions.systemAudio = 'exclude';
	captureOptions.windowAudio = 'exclude';
}

function buildAudioCaptureFailureDebug(
	overrides: {
		platform?: string | null;
		sourceId?: string | null;
		sourceMode?: string | null;
		reason?: string | null;
		detail?: string | null;
	} = {},
): ScreenShareAudioCaptureDebugInfo {
	return {
		platform: overrides.platform ?? getElectronAPI()?.platform ?? null,
		...getLastNativeAudioArmFailure(),
		...overrides,
	};
}

function degradeAudioToVideoOnly(
	captureOptions: ScreenShareCaptureOptions,
	debugInfo: ScreenShareAudioCaptureDebugInfo,
): void {
	logger.warn('Screen share audio capture unavailable; proceeding with video only', debugInfo);
	removeAudioFromCaptureOptions(captureOptions);
}

function failRequestedAudioCapture(debugInfo: ScreenShareAudioCaptureDebugInfo): never {
	logger.warn('Screen share audio capture was requested but could not start', debugInfo);
	throw new ScreenShareAudioCaptureError(debugInfo);
}

function cleanupNativeAudioAfterCaptureDidNotStart(mode: 'start' | 'switch'): void {
	if (mode === 'switch') {
		disarmPendingNativeAudio();
		return;
	}
	disarmNativeAudio();
}

export interface ScreenShareOptionsBuildInput {
	target: ScreenShareTarget;
	sourceDimensions: {width: number; height: number} | null;
	includeAudio: boolean;
	videoCodec: VideoCodec;
	preferredDisplaySurface?: 'window' | 'monitor';
	useBrowserAudioPicker?: boolean;
}

export function buildConfiguredScreenShareOptions(input: ScreenShareOptionsBuildInput): BuiltScreenShareOptions {
	const {captureOptions, publishOptions} = buildScreenShareOptions({
		resolution: input.target.resolution,
		frameRate: input.target.frameRate,
		maxBitrate: input.target.maxBitrate,
		context: input.target.context,
		includeAudio: input.includeAudio,
		contentHint: input.target.contentHint,
		sourceDimensions: input.sourceDimensions ?? undefined,
		preferredDisplaySurface: input.preferredDisplaySurface,
		useBrowserAudioPicker: input.useBrowserAudioPicker,
	});
	publishOptions.videoCodec = input.videoCodec;
	return {captureOptions, publishOptions};
}

interface ConfiguredScreenShareRequest {
	shareContext: ScreenShareContext;
	displayShareEnvironment: DisplayShareEnvironment;
	sourceDimensions?: {width: number; height: number} | null;
	sourceId?: string | null;
	preferredDisplaySurface?: 'window' | 'monitor';
	videoCodec?: VideoCodec;
	includeAudio?: boolean;
}

function resolveConfiguredScreenShareAudio(request: ConfiguredScreenShareRequest): boolean {
	const includeAudio =
		request.includeAudio ??
		shouldIncludeAudioForShare(
			request.shareContext,
			request.displayShareEnvironment,
			request.sourceId,
			request.preferredDisplaySurface,
		);
	if (!includeAudio && request.shareContext !== 'device' && supportsDesktopScreenShareAudioCapture()) {
		logger.info('Screen share audio not requested for this surface', {
			sourceId: request.sourceId,
			preferredDisplaySurface: request.preferredDisplaySurface,
			shareAppAudio: VoiceSettings.getShareAppAudio(),
			shareDesktopAudio: VoiceSettings.getShareDesktopAudio(),
		});
	}
	return includeAudio;
}

async function getConfiguredScreenShareOptions(
	request: ConfiguredScreenShareRequest,
): Promise<BuiltScreenShareOptions & {includeAudio: boolean}> {
	const includeAudio = resolveConfiguredScreenShareAudio(request);
	const sourceDimensions = request.sourceDimensions ?? null;
	const target = resolveConfiguredScreenShareTarget(request.shareContext, sourceDimensions);
	ActiveScreenShareSource.setTarget(target);
	await ScreenShareCodecNegotiation.refreshSelection();
	const {captureOptions, publishOptions} = buildConfiguredScreenShareOptions({
		target,
		sourceDimensions,
		includeAudio,
		videoCodec:
			request.videoCodec ??
			ScreenShareCodecNegotiation.selectScreenShareCodec(VoiceSettings.getPreferredScreenShareCodec()),
		preferredDisplaySurface: request.preferredDisplaySurface,
		useBrowserAudioPicker: request.displayShareEnvironment === 'web',
	});
	return {captureOptions, publishOptions, includeAudio};
}

export interface ConfiguredDisplayScreenShareOptions {
	sourceDimensions?: {
		width: number;
		height: number;
	};
	preferredDisplaySurface?: 'window' | 'monitor';
	isOwnWindow?: boolean;
	includeAudio?: boolean;
}

async function runConfiguredDisplayScreenShare(
	sourceId?: string | null,
	options?: ConfiguredDisplayScreenShareOptions,
	mode: 'start' | 'switch' = 'start',
): Promise<boolean> {
	const electronApi = getElectronAPI();
	const displayShareEnvironment = await getDisplayShareEnvironment();
	const useWaylandPortal = displayShareEnvironment === 'desktop-wayland';
	const restartWaylandPortalForSwitch = useWaylandPortal && mode === 'switch';
	if (electronApi) {
		if (!useWaylandPortal && !sourceId) {
			logger.warn('No desktop source selected for display share');
			return false;
		}
		if (restartWaylandPortalForSwitch) {
			if (!didScreenShareStart()) {
				logger.warn('No active screen share to restart for Wayland portal source switch');
				return false;
			}
			await MediaEngine.setScreenShareEnabled(false, {
				sendUpdate: false,
				playSound: false,
				preserveStreamAudioPreferences: true,
				keepShareTracking: true,
			});
		}
	}
	const {
		captureOptions,
		publishOptions,
		includeAudio: requestedAudio,
	} = await getConfiguredScreenShareOptions({
		shareContext: 'display',
		displayShareEnvironment,
		sourceDimensions: options?.sourceDimensions,
		sourceId,
		preferredDisplaySurface: options?.preferredDisplaySurface,
		includeAudio: options?.includeAudio,
	});
	if (electronApi) {
		let nativeAudioArmed = false;
		const isOwnWindowShare = options?.isOwnWindow === true && sourceId?.startsWith('window:');
		if (isOwnWindowShare && requestedAudio) {
			logger.warn('Fluxer-owned window audio is excluded from screen share capture; continuing video-only', {
				sourceId,
				platform: electronApi.platform,
			});
			removeAudioFromCaptureOptions(captureOptions);
		}
		const requestedAppAudioOnLinux =
			requestedAudio && !isOwnWindowShare && electronApi.platform === 'linux' && sourceId?.startsWith('window:');
		const requestedDesktopAudio =
			requestedAudio && (sourceId?.startsWith('screen:') || (useWaylandPortal && VoiceSettings.getShareDesktopAudio()));
		const requestedNativeDesktopAudio =
			requestedDesktopAudio &&
			(electronApi.platform === 'darwin' || electronApi.platform === 'win32') &&
			sourceId?.startsWith('screen:');
		const requestedNativePickerAudioOnLinux = requestedAudio && electronApi.platform === 'linux' && useWaylandPortal;
		const appShareAudioScope = requestedAppAudioOnLinux
			? await resolveAppShareAudioScope(ActiveScreenShareSource.getPendingWindowAudioScope())
			: null;
		const appShareAudioRoute = appShareAudioScope == null ? null : selectAppShareAudioRouteForScope(appShareAudioScope);
		const linuxAudioSourceMode = requestedAppAudioOnLinux
			? appShareAudioRouteToSourceMode(appShareAudioRoute)
			: electronApi.platform === 'linux' && (requestedDesktopAudio || requestedNativePickerAudioOnLinux)
				? VoiceSettings.getEffectiveScreenShareAudioSourceMode()
				: null;
		if (requestedAppAudioOnLinux && appShareAudioRoute === 'window') {
			try {
				nativeAudioArmed = await armNativeAudioForNextCapture(sourceId ?? '');
			} catch (error) {
				logger.warn('Failed to arm Linux native per-window audio capture', {
					sourceId,
					error,
				});
			}
			if (!nativeAudioArmed) {
				failRequestedAudioCapture(
					buildAudioCaptureFailureDebug({
						sourceId,
						reason: getLastNativeAudioArmFailure()?.reason ?? 'linux-window-audio-route-unavailable',
					}),
				);
			}
		} else if (requestedNativeDesktopAudio) {
			try {
				nativeAudioArmed = await armNativeSystemAudioForNextCapture();
			} catch (error) {
				logger.warn('Failed to arm native desktop audio capture', {
					sourceId,
					platform: electronApi.platform,
					error,
				});
			}
			if (!nativeAudioArmed) {
				const debugInfo = buildAudioCaptureFailureDebug({
					sourceId,
					sourceMode: 'system',
					platform: electronApi.platform,
					reason: getLastNativeAudioArmFailure()?.reason ?? 'system-audio-route-unavailable',
				});
				logger.warn('Desktop audio unavailable; aborting screen share because audio was requested', debugInfo);
				failRequestedAudioCapture(debugInfo);
			}
		} else if (linuxAudioSourceMode === 'none') {
			removeAudioFromCaptureOptions(captureOptions);
		} else if (linuxAudioSourceMode !== null && electronApi.platform === 'linux') {
			const sourceMode = linuxAudioSourceMode;
			const userIncludeSources = VoiceSettings.getEffectiveScreenShareAudioIncludeSources().map((entry) => ({
				...entry,
			}));
			const userExcludeSources = VoiceSettings.getEffectiveScreenShareAudioExcludeSources().map((entry) => ({
				...entry,
			}));
			if (requestedNativePickerAudioOnLinux && options?.preferredDisplaySurface === 'window') {
				logger.info(
					'Wayland window share cannot identify the shared window; capturing the desktop mix without Fluxer instead',
					{sourceMode},
				);
			}
			try {
				nativeAudioArmed = await armNativeAudioForLinuxRouting(
					buildLinuxNativeAudioRule(sourceMode, userIncludeSources, userExcludeSources),
				);
			} catch (error) {
				logger.warn('Failed to arm Linux native audio-capture link', {
					sourceMode,
					error,
				});
			}
			if (!nativeAudioArmed) {
				failRequestedAudioCapture(
					buildAudioCaptureFailureDebug({
						sourceId,
						sourceMode,
						reason: getLastNativeAudioArmFailure()?.reason ?? 'linux-system-audio-route-unavailable',
					}),
				);
			}
		}
		if (
			requestedAudio &&
			!isOwnWindowShare &&
			sourceId?.startsWith('window:') &&
			(electronApi.platform === 'darwin' || electronApi.platform === 'win32')
		) {
			try {
				nativeAudioArmed = await armNativeAudioForNextCapture(sourceId);
			} catch (error) {
				logger.warn('Failed to arm native per-window audio capture', {
					sourceId,
					error,
				});
			}
			if (!nativeAudioArmed) {
				const debugInfo = buildAudioCaptureFailureDebug({
					sourceId,
					reason: getLastNativeAudioArmFailure()?.reason ?? 'native-window-audio-route-unavailable',
				});
				logger.warn('Per-window audio unavailable; aborting screen share because audio was requested', {
					sourceId,
					platform: electronApi.platform,
					reason: debugInfo.reason,
				});
				failRequestedAudioCapture(debugInfo);
			}
		}
		if (
			requestedAudio &&
			sourceId?.startsWith('window:') &&
			!isOwnWindowShare &&
			!nativeAudioArmed &&
			electronApi.platform !== 'darwin' &&
			electronApi.platform !== 'win32' &&
			electronApi.platform !== 'linux'
		) {
			failRequestedAudioCapture(
				buildAudioCaptureFailureDebug({
					sourceId,
					platform: electronApi.platform,
					reason: getLastNativeAudioArmFailure()?.reason ?? 'window-audio-route-unavailable',
				}),
			);
		}
		if (requestedAudio && (nativeAudioArmed || electronApi.platform === 'linux')) {
			removeAudioFromCaptureOptions(captureOptions);
		}
		try {
			if (useWaylandPortal && options?.preferredDisplaySurface) {
				await electronApi.setDisplayMediaPortalPreference?.(options.preferredDisplaySurface);
			}
			if (!useWaylandPortal && sourceId) {
				setDesktopSourceIntent({sourceId, includeAudio: false});
			}
			let operationSucceeded = false;
			if (mode === 'switch' && !restartWaylandPortalForSwitch) {
				operationSucceeded = await MediaEngine.replaceActiveDisplayScreenShare(captureOptions, publishOptions, {
					sourceId: sourceId ?? null,
					displayShareEnvironment,
					requireAudio: requestedAudio && nativeAudioArmed,
				});
			} else {
				await MediaEngine.setScreenShareEnabled(
					true,
					restartWaylandPortalForSwitch ? {...captureOptions, playSound: false} : captureOptions,
					publishOptions,
				);
				operationSucceeded = didScreenShareStart();
			}
			const captured = mode === 'switch' ? operationSucceeded : didScreenShareStart();
			if (nativeAudioArmed && !captured) {
				cleanupNativeAudioAfterCaptureDidNotStart(mode);
			}
			if (captured && !useWaylandPortal && sourceId) {
				ActiveScreenShareSource.setPublishedSource(sourceId.startsWith('window:') ? 'app' : 'display', sourceId, {
					isOwnWindow: isOwnWindowShare,
				});
				ActiveScreenShareSource.setSourceDimensions(options?.sourceDimensions ?? null);
				ActiveScreenShareSource.setWindowAudioScope(appShareAudioScope ?? 'window');
			}
			if (captured && useWaylandPortal) {
				ActiveScreenShareSource.setPublishedSource('wayland', null);
				ActiveScreenShareSource.setSourceDimensions(null);
			}
			if (!captured && useWaylandPortal && mode !== 'switch') {
				logger.warn('Wayland screen share portal did not yield a capturable source', {sourceId});
				throw new ScreenSharePortalUnavailableError('empty');
			}
			if (
				captured &&
				requestedAudio &&
				electronApi.platform === 'linux' &&
				linuxAudioSourceMode !== null &&
				linuxAudioSourceMode !== 'none'
			) {
				const audioRelinked = await reconfigureActiveLinuxScreenShareAudioLink().catch((error) => {
					logger.warn('Failed to link Linux screen-share audio after capture start', {mode, error});
					return false;
				});
				if (!audioRelinked) {
					const debugInfo = buildAudioCaptureFailureDebug({
						sourceMode: linuxAudioSourceMode,
						platform: electronApi.platform,
						reason: getLastNativeAudioArmFailure()?.reason ?? 'linux-system-audio-route-unavailable',
					});
					logger.warn('Linux screen-share capture succeeded, but audio link did not complete', {
						...debugInfo,
						mode,
					});
					degradeAudioToVideoOnly(captureOptions, debugInfo);
				}
			}
			return captured;
		} catch (error) {
			if (isScreenSharePortalUnavailableError(error)) {
				throw error;
			}
			const capturedAfterError = didScreenShareStart();
			if (nativeAudioArmed && !capturedAfterError) {
				cleanupNativeAudioAfterCaptureDidNotStart(mode);
			}
			if (useWaylandPortal && !capturedAfterError && mode !== 'switch') {
				logger.warn('Wayland screen share portal capture failed to start', {
					sourceId,
					error,
				});
				throw new ScreenSharePortalUnavailableError('error', error instanceof Error ? error.message : undefined);
			}
			throw error;
		} finally {
			if (!useWaylandPortal) {
				clearDesktopSourceIntent();
			}
		}
	}
	let operationSucceeded = false;
	if (mode === 'switch') {
		operationSucceeded = await MediaEngine.replaceActiveDisplayScreenShare(captureOptions, publishOptions, {
			sourceId: null,
			displayShareEnvironment,
			requireAudio: false,
		});
	} else {
		await MediaEngine.setScreenShareEnabled(true, captureOptions, publishOptions);
		operationSucceeded = didScreenShareStart();
	}
	const captured = mode === 'switch' ? operationSucceeded : didScreenShareStart();
	if (captured) {
		ActiveScreenShareSource.setPublishedSource('web', null);
		ActiveScreenShareSource.setSourceDimensions(null);
	}
	return captured;
}

interface ConfiguredScreenShareMutationRequest {
	execute: () => Promise<boolean>;
	promise: Promise<boolean>;
	resolve: (result: boolean) => void;
	reject: (error: unknown) => void;
}

let configuredScreenShareMutationActive = false;
let pendingConfiguredScreenShareMutation: ConfiguredScreenShareMutationRequest | null = null;

function createConfiguredScreenShareMutationRequest(
	execute: () => Promise<boolean>,
): ConfiguredScreenShareMutationRequest {
	let resolveRequest: ((result: boolean) => void) | undefined;
	let rejectRequest: ((error: unknown) => void) | undefined;
	const promise = new Promise<boolean>((resolve, reject) => {
		resolveRequest = resolve;
		rejectRequest = reject;
	});
	if (!resolveRequest || !rejectRequest) {
		throw new Error('Configured screen share mutation deferred was not initialized');
	}
	return {execute, promise, resolve: resolveRequest, reject: rejectRequest};
}

async function drainConfiguredScreenShareMutations(
	initialRequest: ConfiguredScreenShareMutationRequest,
): Promise<void> {
	let request: ConfiguredScreenShareMutationRequest | null = initialRequest;
	while (request) {
		try {
			request.resolve(await request.execute());
		} catch (error) {
			request.reject(error);
		}
		request = pendingConfiguredScreenShareMutation;
		pendingConfiguredScreenShareMutation = null;
	}
	configuredScreenShareMutationActive = false;
}

function restoreCommittedScreenShareTarget(committed: ScreenShareTarget | null): void {
	if (didScreenShareStart()) {
		ActiveScreenShareSource.setTarget(committed);
		return;
	}
	ActiveScreenShareSource.clear();
}

async function runConfiguredScreenShareMutation(execute: () => Promise<boolean>): Promise<boolean> {
	const committed = ActiveScreenShareSource.getTarget();
	try {
		const operationSucceeded = await execute();
		if (!operationSucceeded) {
			restoreCommittedScreenShareTarget(committed);
		}
		return operationSucceeded;
	} catch (error) {
		restoreCommittedScreenShareTarget(committed);
		throw error;
	}
}

export function scheduleConfiguredScreenShareMutation(execute: () => Promise<boolean>): Promise<boolean> {
	const request = createConfiguredScreenShareMutationRequest(() => runConfiguredScreenShareMutation(execute));
	if (!configuredScreenShareMutationActive) {
		configuredScreenShareMutationActive = true;
		void drainConfiguredScreenShareMutations(request);
		return request.promise;
	}
	pendingConfiguredScreenShareMutation?.resolve(false);
	pendingConfiguredScreenShareMutation = request;
	return request.promise;
}

export async function startConfiguredDisplayScreenShare(
	sourceId?: string | null,
	options?: ConfiguredDisplayScreenShareOptions,
): Promise<boolean> {
	return scheduleConfiguredScreenShareMutation(async () => {
		let didStart = false;
		await executeScreenShareOperation(async () => {
			didStart = await runConfiguredDisplayScreenShare(sourceId, options, 'start');
		});
		return didStart;
	});
}

export async function switchConfiguredDisplayScreenShare(
	sourceId?: string | null,
	options?: ConfiguredDisplayScreenShareOptions,
): Promise<boolean> {
	return scheduleConfiguredScreenShareMutation(async () => {
		let didSwitch = false;
		await executeScreenShareOperation(async () => {
			didSwitch = await runConfiguredDisplayScreenShare(sourceId, options, 'switch');
		});
		return didSwitch;
	});
}

export async function restartActiveScreenShareCapture(): Promise<boolean> {
	if (!didScreenShareStart()) return false;
	const publishedSource = ActiveScreenShareSource.getPublishedSource();
	const sourceId = ActiveScreenShareSource.getSourceId();
	if ((publishedSource !== 'app' && publishedSource !== 'display') || sourceId == null) {
		logger.warn('The live capture cannot take a new geometry, and restarting it would re-open the source picker', {
			publishedSource,
		});
		return false;
	}
	logger.info('Restarting the live screen share capture because its geometry did not take', {
		publishedSource,
		sourceId,
	});
	try {
		return await runConfiguredDisplayScreenShare(
			sourceId,
			{
				sourceDimensions: ActiveScreenShareSource.getSourceDimensions() ?? undefined,
				preferredDisplaySurface: publishedSource === 'app' ? 'window' : 'monitor',
				isOwnWindow: ActiveScreenShareSource.isOwnWindow(),
			},
			'switch',
		);
	} catch (error) {
		if (isScreenShareRollbackIncompleteError(error)) handleScreenShareError(error);
		logger.warn('Failed to restart the live screen share capture for a settings change', error);
		return false;
	}
}

async function linkManualAudioSourcesForDeviceShare(mode: 'start' | 'switch'): Promise<void> {
	const linked = await reconfigureActiveLinuxScreenShareAudioLink().catch((error) => {
		logger.warn('Failed to link the selected application audio to the device share', {mode, error});
		return false;
	});
	if (linked) return;
	logger.warn(
		'Device screen share is running without the selected application audio',
		buildAudioCaptureFailureDebug({
			sourceMode: VoiceSettings.getEffectiveScreenShareAudioSourceMode(),
			reason: getLastNativeAudioArmFailure()?.reason ?? 'manual-audio-route-unavailable',
		}),
	);
}

async function resolveDeviceShareAudioDeviceId(videoDeviceId: string): Promise<string | undefined> {
	const chosenAudioDeviceId = VoiceSettings.getScreenShareAudioDeviceId();
	if (chosenAudioDeviceId && chosenAudioDeviceId !== 'default') return chosenAudioDeviceId;
	if (VoiceSettings.getScreenShareDeviceAudioUsesMicrophone()) return VoiceSettings.getInputDeviceId() || undefined;
	if (!videoDeviceId || videoDeviceId === 'default') return undefined;
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		const pairedInput = findPairedDeviceShareAudioInput(devices, videoDeviceId);
		if (!pairedInput) {
			logger.info('The shared capture device has no audio input of its own, sharing it without audio', {
				videoDeviceId,
			});
			return undefined;
		}
		logger.info('Using the capture device own audio input for the device share', {
			videoDeviceId,
			audioDeviceId: pairedInput.deviceId,
		});
		return pairedInput.deviceId;
	} catch (error) {
		logger.warn('Failed to pair an audio input with the shared video device', {videoDeviceId, error});
		return undefined;
	}
}

export async function reconfigureActiveDeviceShareAudio(): Promise<boolean> {
	if (!VoiceSettings.getShareDeviceAudio()) return false;
	if (await shouldRouteManualAudioSourcesForShare('device')) {
		return reconfigureActiveLinuxScreenShareAudioLink();
	}
	await stopActiveLinuxScreenShareAudioLink();
	const audioDeviceId = await resolveDeviceShareAudioDeviceId(MediaEngine.getActiveScreenShareVideoDeviceId());
	if (audioDeviceId === undefined) {
		MediaEngine.setScreenShareAudioMuted(true);
		return true;
	}
	return MediaEngine.ensureDeviceScreenShareMicPublication(audioDeviceId);
}

async function getConfiguredDeviceScreenShareAudio(videoDeviceId: string): Promise<{
	routeManualAudioSources: boolean;
	audioDeviceId: string | undefined;
}> {
	if (!VoiceSettings.getShareDeviceAudio()) return {routeManualAudioSources: false, audioDeviceId: undefined};
	if (await shouldRouteManualAudioSourcesForShare('device')) {
		return {routeManualAudioSources: true, audioDeviceId: undefined};
	}
	return {routeManualAudioSources: false, audioDeviceId: await resolveDeviceShareAudioDeviceId(videoDeviceId)};
}

export async function startConfiguredDeviceScreenShare(videoDeviceId: string): Promise<boolean> {
	return scheduleConfiguredScreenShareMutation(async () => {
		const {captureOptions, publishOptions} = await getConfiguredScreenShareOptions({
			shareContext: 'device',
			displayShareEnvironment: 'desktop-custom',
		});
		const {routeManualAudioSources, audioDeviceId} = await getConfiguredDeviceScreenShareAudio(videoDeviceId);
		try {
			await MediaEngine.startDeviceScreenShare(
				{
					videoDeviceId,
					audioDeviceId,
					resolution: captureOptions.resolution,
				},
				publishOptions,
			);
		} catch (error) {
			logger.error('Failed to start device screen share', {
				error,
				videoDeviceId,
			});
		}
		const didStart = didScreenShareStart();
		if (didStart) {
			ActiveScreenShareSource.setPublishedSource('device', null);
			ActiveScreenShareSource.setSourceDimensions(null);
		}
		if (didStart && routeManualAudioSources) await linkManualAudioSourcesForDeviceShare('start');
		return didStart;
	});
}

export async function switchConfiguredDeviceScreenShare(videoDeviceId: string): Promise<boolean> {
	return scheduleConfiguredScreenShareMutation(async () => {
		const {captureOptions, publishOptions} = await getConfiguredScreenShareOptions({
			shareContext: 'device',
			displayShareEnvironment: 'desktop-custom',
		});
		const {routeManualAudioSources, audioDeviceId} = await getConfiguredDeviceScreenShareAudio(videoDeviceId);
		try {
			const didSwitch = await MediaEngine.replaceActiveDeviceScreenShare(
				{
					videoDeviceId,
					audioDeviceId,
					resolution: captureOptions.resolution,
				},
				publishOptions,
			);
			if (didSwitch) {
				ActiveScreenShareSource.setPublishedSource('device', null);
				ActiveScreenShareSource.setSourceDimensions(null);
			}
			if (didSwitch && routeManualAudioSources) await linkManualAudioSourcesForDeviceShare('switch');
			return didSwitch;
		} catch (error) {
			logger.error('Failed to switch device screen share source', {
				error,
				videoDeviceId,
			});
			return false;
		}
	});
}
